import {
  Badge,
  Button,
  Card,
  Collapsible,
  DataTable,
  Layout,
  TextStyle,
} from "@shopify/polaris";
import React, { useState } from "react";

const ChangeSummary = ({ importHistory }) => {
  const [expandedSections, setExpandedSections] = useState({});

  // Debug logging
  console.log("ChangeSummary - importHistory:", importHistory);
  console.log("ChangeSummary - change_summary:", importHistory?.change_summary);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatPrice = (price) => {
    if (!price) return "-";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const renderPriceChanges = () => {
    const priceChanges =
      importHistory.change_summary?.price_changes?.details || [];

    if (priceChanges.length === 0) {
      return <TextStyle variation="subdued">No price changes</TextStyle>;
    }

    const rows = priceChanges.map((change, index) => [
      <TextStyle variation="code">{change.sku}</TextStyle>,
      change.product_name || "-",
      formatPrice(change.from),
      formatPrice(change.to),
      <Badge status={change.change_amount > 0 ? "success" : "critical"}>
        {change.change_amount > 0 ? "+" : ""}
        {formatPrice(change.change_amount)}
      </Badge>,
      change.change_percentage ? (
        <Badge status={change.change_percentage > 0 ? "success" : "critical"}>
          {change.change_percentage > 0 ? "+" : ""}
          {change.change_percentage}%
        </Badge>
      ) : (
        "-"
      ),
    ]);

    return (
      <DataTable
        columnContentTypes={["text", "text", "text", "text", "text", "text"]}
        headings={["SKU", "Product Name", "From", "To", "Change", "Percentage"]}
        rows={rows}
      />
    );
  };

  const renderInventoryChanges = () => {
    const inventoryChanges =
      importHistory.change_summary?.inventory_changes?.details || [];

    if (inventoryChanges.length === 0) {
      return <TextStyle variation="subdued">No inventory changes</TextStyle>;
    }

    const rows = inventoryChanges.map((change, index) => [
      <TextStyle variation="code">{change.sku}</TextStyle>,
      change.product_name || "-",
      change.from,
      change.to,
      <Badge status={change.change_amount > 0 ? "success" : "critical"}>
        {change.change_amount > 0 ? "+" : ""}
        {change.change_amount}
      </Badge>,
    ]);

    return (
      <DataTable
        columnContentTypes={["text", "text", "text", "text", "text"]}
        headings={["SKU", "Product Name", "From", "To", "Change"]}
        rows={rows}
      />
    );
  };

  const renderPromotionalChanges = () => {
    const promotionalChanges =
      importHistory.change_summary?.promotional_changes?.details || [];

    if (promotionalChanges.length === 0) {
      return <TextStyle variation="subdued">No promotional changes</TextStyle>;
    }

    const rows = promotionalChanges.map((change, index) => [
      <TextStyle variation="code">{change.sku}</TextStyle>,
      change.product_name || "-",
      <Badge
        status={
          change.type === "promotion_added"
            ? "success"
            : change.type === "promotion_removed"
            ? "critical"
            : "warning"
        }
      >
        {change.type.replace("_", " ")}
      </Badge>,
      formatPrice(change.from),
      formatPrice(change.to),
    ]);

    return (
      <DataTable
        columnContentTypes={["text", "text", "text", "text", "text"]}
        headings={["SKU", "Product Name", "Type", "From", "To"]}
        rows={rows}
      />
    );
  };

  const renderProductTypeChanges = () => {
    const productTypeChanges =
      importHistory.change_summary?.product_type_changes?.details || [];

    if (productTypeChanges.length === 0) {
      return <TextStyle variation="subdued">No product type changes</TextStyle>;
    }

    const rows = productTypeChanges.map((change, index) => [
      <TextStyle variation="code">{change.sku}</TextStyle>,
      change.product_name || "-",
      change.from || "-",
      change.to || "-",
    ]);

    return (
      <DataTable
        columnContentTypes={["text", "text", "text", "text"]}
        headings={["SKU", "Product Name", "From", "To"]}
        rows={rows}
      />
    );
  };

  const changeSummary = importHistory.change_summary || {};

  return (
    <div>
      <Layout>
        <Layout.Section>
          <Card title="Change Summary">
            <Card.Section>
              <Layout>
                <Layout.Section oneQuarter>
                  <div style={{ textAlign: "center" }}>
                    <TextStyle variation="headingLg" tone="info">
                      {changeSummary.price_changes?.count || 0}
                    </TextStyle>
                    <p>Price Changes</p>
                  </div>
                </Layout.Section>
                <Layout.Section oneQuarter>
                  <div style={{ textAlign: "center" }}>
                    <TextStyle variation="headingLg" tone="warning">
                      {changeSummary.inventory_changes?.count || 0}
                    </TextStyle>
                    <p>Inventory Changes</p>
                  </div>
                </Layout.Section>
                <Layout.Section oneQuarter>
                  <div style={{ textAlign: "center" }}>
                    <TextStyle variation="headingLg" tone="success">
                      {changeSummary.promotional_changes?.count || 0}
                    </TextStyle>
                    <p>Promotional Changes</p>
                  </div>
                </Layout.Section>
                <Layout.Section oneQuarter>
                  <div style={{ textAlign: "center" }}>
                    <TextStyle variation="headingLg" tone="base">
                      {changeSummary.product_type_changes?.count || 0}
                    </TextStyle>
                    <p>Product Type Changes</p>
                  </div>
                </Layout.Section>
              </Layout>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>

      <Layout>
        <Layout.Section>
          <Card>
            <Card.Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <TextStyle variation="headingMd">Price Changes</TextStyle>
                <Button size="slim" onClick={() => toggleSection("price")}>
                  {expandedSections.price ? "Collapse" : "Expand"}
                </Button>
              </div>
              <Collapsible open={expandedSections.price}>
                {renderPriceChanges()}
              </Collapsible>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>

      <Layout>
        <Layout.Section>
          <Card>
            <Card.Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <TextStyle variation="headingMd">Inventory Changes</TextStyle>
                <Button size="slim" onClick={() => toggleSection("inventory")}>
                  {expandedSections.inventory ? "Collapse" : "Expand"}
                </Button>
              </div>
              <Collapsible open={expandedSections.inventory}>
                {renderInventoryChanges()}
              </Collapsible>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>

      <Layout>
        <Layout.Section>
          <Card>
            <Card.Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <TextStyle variation="headingMd">Promotional Changes</TextStyle>
                <Button
                  size="slim"
                  onClick={() => toggleSection("promotional")}
                >
                  {expandedSections.promotional ? "Collapse" : "Expand"}
                </Button>
              </div>
              <Collapsible open={expandedSections.promotional}>
                {renderPromotionalChanges()}
              </Collapsible>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>

      <Layout>
        <Layout.Section>
          <Card>
            <Card.Section>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <TextStyle variation="headingMd">
                  Product Type Changes
                </TextStyle>
                <Button
                  size="slim"
                  onClick={() => toggleSection("productType")}
                >
                  {expandedSections.productType ? "Collapse" : "Expand"}
                </Button>
              </div>
              <Collapsible open={expandedSections.productType}>
                {renderProductTypeChanges()}
              </Collapsible>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </div>
  );
};

export default ChangeSummary;
